import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Trash2 } from 'lucide-react';
import { Badge } from '@components/atoms/ui-kit/badge.tsx';
import { Card } from '@components/atoms/ui-kit/card.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { ModelData } from '@utils/api/custom-models/use-get-custom-models-data.tsx';
import { CustomModelDataType } from '@utils/api/custom-models/use-update-custom-model.tsx';
import { formatDate } from '@utils/date.ts';
import {
  ContributionSource,
  ContributionStatusBadge,
  LabelledStat,
  formatClipDuration,
  formatClipTimeRange,
  isPendingContribution,
  stemOf
} from './contributions-list-parts.tsx';
import { isContribution } from '@utils/editors/contribution-messages.ts';

interface Props {
  rows: ModelData[];
  /** Round number the model is currently on. Contributions with a
   *  round_number greater than every completed round's number (or NULL)
   *  are surfaced as "pending" — the loop the customer is trying to close. */
  lastCompletedRound: number | null;
  onDelete: (row: ModelData) => void;
  isDeleting: boolean;
}

/**
 * The clips list on the custom-model page (#2211).
 *
 * A contributed clip is a LanguageConfigData row with
 * ``source_kind`` ``editor_clip`` or ``editor_file`` (a whole recording).
 * Each creates two rows (audio +
 * transcript) sharing a stem; the list is built off the audio row so a
 * contribution shows once, and the transcript row is implicit.
 *
 * Per contribution: the corrected term (``anchor_text``, not the filename —
 * "Kožul" is what the customer recognises, ``clip-8831.m4a`` is not), the
 * source session (linked when it still exists, "source no longer available"
 * when retention has aged it out — that is normal, not an error), the clip
 * duration, when it was contributed, its processing status, and a delete
 * button (removes the storage object, not just the row).
 */
export const ContributionsList = ({
  rows,
  lastCompletedRound,
  onDelete,
  isDeleting
}: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  // Build a stem → anchor_text lookup from the transcript rows so a
  // missing anchor_text on the audio row falls back to its paired
  // transcript row. Every contribution creates two rows sharing a
  // filename stem (``{stem}.m4a`` + ``{stem}.vtt``); the transcript
  // row has always carried the anchor_text, but the audio row only
  // started carrying it after the recent backend patch. Cross-
  // referencing here means the panel shows the correct term for
  // historical contributions too — no backfill migration needed.
  const anchorByStem = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rows) {
      if (!isContribution(r.source_kind)) continue;
      if (r.type !== CustomModelDataType.TRANSCRIPT) continue;
      const anchor = r.anchor_text?.trim();
      if (!anchor) continue;
      m.set(stemOf(r.name), anchor);
    }
    return m;
  }, [rows]);

  const clips = useMemo(
    () =>
      rows
        .filter(
          r =>
            isContribution(r.source_kind) &&
            r.type === CustomModelDataType.AUDIO
        )
        .sort((a, b) => {
          const at = a.created_at ? Date.parse(a.created_at) : 0;
          const bt = b.created_at ? Date.parse(b.created_at) : 0;
          return bt - at;
        }),
    [rows]
  );

  if (clips.length === 0) {
    return (
      <Card padding="none" className="p-6">
        <Text as="p" size="sm" tone="muted">
          {t('contributions-empty')}
        </Text>
        <Text as="p" size="xs" tone="muted" className="mt-1">
          {t('contributions-empty-hint')}
        </Text>
      </Card>
    );
  }

  return (
    <Card padding="none" className="overflow-hidden">
      <ul aria-label={t('contributions-list')}>
        {clips.map((clip, i) => {
          const isPending = isPendingContribution(clip, lastCompletedRound);
          // Fall back to the paired transcript row's anchor_text if the
          // audio row's is null (historical contributions written before
          // the backend patch). See the ``anchorByStem`` map above.
          const displayAnchor =
            clip.anchor_text?.trim() || anchorByStem.get(stemOf(clip.name));
          // ``divide-y`` on the <ul> applied 1px borders in the browser's
          // default ``currentColor`` — which is the text colour, and rendered
          // as a heavy near-black rule between rows in dark mode. Apply the
          // hairline as an inline top border on every row except the first
          // so the divider tracks the theme's chosen divider weight.
          return (
            <li
              key={clip.id}
              className="p-3 flex items-start gap-3"
              style={
                i === 0
                  ? undefined
                  : {
                      borderTop: `1px solid ${getColour('hairline')}`
                    }
              }
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <Text
                    as="span"
                    size="sm"
                    weight="semibold"
                    className="truncate"
                    title={displayAnchor ?? clip.name}
                  >
                    {displayAnchor ?? (
                      <Text as="span" tone="muted" className="italic">
                        {t('contribution-anchor-missing')}
                      </Text>
                    )}
                  </Text>
                  <ContributionStatusBadge status={clip.status} />
                  {isPending && (
                    <Tooltip
                      label={t('contribution-pending-hint')}
                      placement="top"
                    >
                      <Badge color="amber">{t('contribution-pending')}</Badge>
                    </Tooltip>
                  )}
                  {clip.round_number != null && !isPending && (
                    <Badge color="zinc">#{clip.round_number}</Badge>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  <ContributionSource clip={clip} />
                  {clip.start_time != null && clip.end_time != null && (
                    <LabelledStat
                      label={t('contribution-timecode-label')}
                      value={formatClipTimeRange(
                        clip.start_time,
                        clip.end_time
                      )}
                      title={t('contribution-source-timecode')}
                      tabularNums
                    />
                  )}
                  {clip.duration != null && (
                    <LabelledStat
                      label={t('contribution-length-label')}
                      value={formatClipDuration(clip.duration)}
                    />
                  )}
                  {clip.created_at && (
                    <LabelledStat
                      label={t('contribution-added-label')}
                      value={formatDate(clip.created_at)}
                    />
                  )}
                </div>
                {clip.error && (
                  <div className="mt-1 flex items-center gap-1.5">
                    <AlertCircle
                      className="h-3.5 w-3.5"
                      style={{ color: getColour('danger') }}
                      aria-hidden="true"
                    />
                    <Text
                      as="span"
                      size="xs"
                      style={{ color: getColour('danger') }}
                    >
                      {clip.error}
                    </Text>
                  </div>
                )}
              </div>

              <Button
                onClick={() => onDelete(clip)}
                icon={<Trash2 width={14} />}
                size="xs"
                variant="ghost"
                disabled={isDeleting}
                aria-label={t('remove-contribution', {
                  term: clip.anchor_text || clip.name
                })}
                title={t('remove-contribution', {
                  term: clip.anchor_text || clip.name
                })}
              />
            </li>
          );
        })}
      </ul>
    </Card>
  );
};
